import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, ImageOff, Newspaper, Share2, UserRound } from "lucide-react";
import { api, assetUrl } from "../lib/api";

function formatDate(value) {
  if (!value) return "Recently published";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "long", day: "numeric" });
}

function newsDate(item) {
  return item?.published_at || item?.created_at || item?.updated_at;
}

function getSummary(item) {
  return item?.summary || item?.content?.slice(0, 180) || "Official Federal Polytechnic Bida campus update.";
}

function normalizeParagraphs(value) {
  return String(value || "")
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function NewsImage({ item, compact = false }) {
  const image = assetUrl(item?.image_url);
  const [broken, setBroken] = useState(false);

  if (!image || broken) {
    return (
      <div className={`flex ${compact ? "h-full min-h-[150px]" : "min-h-[360px] md:min-h-[500px]"} items-center justify-center bg-gradient-to-br from-blue-950 via-indigo-950 to-slate-950 p-8 text-center text-white`}>
        <div>
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/15">
            <ImageOff className="h-9 w-9 text-blue-200" />
          </div>
          <p className="text-sm font-black uppercase tracking-[0.24em] text-blue-200">Federal Polytechnic Bida</p>
          <h3 className="mt-2 text-2xl font-black">Image currently unavailable</h3>
        </div>
      </div>
    );
  }

  return (
    <img
      src={image}
      alt={item?.title || "Campus news"}
      onError={() => setBroken(true)}
      className={`${compact ? "h-full min-h-[150px]" : "h-full min-h-[360px] md:min-h-[500px]"} w-full object-cover`}
    />
  );
}

export default function NewsDetail() {
  const { id } = useParams();
  const [newsItem, setNewsItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    async function loadNewsDetail() {
      setLoading(true);
      setError("");
      try {
        const [detail, allNews] = await Promise.all([
          api(`/api/news/${id}`),
          api("/api/news?limit=6").catch(() => []),
        ]);
        if (!mounted) return;
        setNewsItem(detail);
        setRelated(Array.isArray(allNews) ? allNews.filter((item) => String(item.id) !== String(detail.id) && item.slug !== detail.slug).slice(0, 3) : []);
      } catch (err) {
        if (!mounted) return;
        console.error("Error fetching news detail:", err);
        setError(err.message || "News could not be loaded.");
        setNewsItem(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadNewsDetail();
    return () => {
      mounted = false;
    };
  }, [id]);

  const paragraphs = useMemo(() => normalizeParagraphs(newsItem?.content || newsItem?.summary), [newsItem]);

  if (loading) {
    return (
      <main className="min-h-screen bg-slate-50">
        <section className="mx-auto max-w-6xl px-4 py-12 md:px-6">
          <div className="animate-pulse overflow-hidden rounded-[2rem] bg-white shadow-sm ring-1 ring-slate-200">
            <div className="h-[420px] bg-slate-200" />
            <div className="space-y-4 p-8">
              <div className="h-6 w-40 rounded bg-slate-200" />
              <div className="h-12 w-3/4 rounded bg-slate-200" />
              <div className="h-5 w-full rounded bg-slate-200" />
              <div className="h-5 w-5/6 rounded bg-slate-200" />
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (error || !newsItem) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-16">
        <div className="mx-auto max-w-2xl rounded-[2rem] bg-white p-8 text-center shadow-xl ring-1 ring-slate-200">
          <Newspaper className="mx-auto mb-4 h-12 w-12 text-blue-700" />
          <h1 className="text-2xl font-black text-slate-950">News not found</h1>
          <p className="mt-3 text-slate-600">{error || "The news story could not be loaded."}</p>
          <Link to="/news" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-blue-700 px-5 py-3 font-black text-white hover:bg-blue-800">
            <ArrowLeft className="h-4 w-4" /> Back to News
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#f6f8fc] text-slate-950">
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(37,99,235,.35),transparent_30%),radial-gradient(circle_at_88%_10%,rgba(147,51,234,.25),transparent_30%),linear-gradient(135deg,#06111f,#0f1b3a_55%,#1e1b4b)]" />
        <div className="relative mx-auto grid max-w-7xl gap-8 px-4 py-10 md:px-6 lg:grid-cols-[1fr_440px] lg:items-center lg:py-16">
          <div>
            <Link to="/news" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-black text-blue-100 backdrop-blur hover:bg-white/15">
              <ArrowLeft className="h-4 w-4" /> Back to Newsroom
            </Link>
            <p className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-500/20 px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-blue-100 ring-1 ring-blue-300/20">
              <Newspaper className="h-4 w-4" /> Official Campus News
            </p>
            <h1 className="mt-5 max-w-4xl text-4xl font-black leading-[1.02] tracking-tight md:text-6xl">{newsItem.title}</h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">{getSummary(newsItem)}</p>
            <div className="mt-7 flex flex-wrap gap-3 text-sm font-bold text-slate-200">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2"><Calendar className="h-4 w-4 text-blue-200" /> {formatDate(newsDate(newsItem))}</span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2"><UserRound className="h-4 w-4 text-blue-200" /> {newsItem.author || "Federal Polytechnic Bida"}</span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2"><Clock className="h-4 w-4 text-blue-200" /> Official update</span>
            </div>
          </div>
          <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/10 p-3 shadow-2xl shadow-black/30 backdrop-blur">
            <div className="overflow-hidden rounded-[1.5rem]"><NewsImage item={newsItem} compact /></div>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-4 py-10 md:px-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <article className="overflow-hidden rounded-[2rem] bg-white shadow-xl shadow-slate-200/60 ring-1 ring-slate-200">
          <div className="border-b border-slate-100 px-6 py-5 md:px-10">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-blue-700">Full Story</p>
          </div>
          <div className="p-6 md:p-10">
            <div className="prose prose-slate max-w-none prose-p:text-lg prose-p:leading-9 prose-p:text-slate-700 prose-strong:text-slate-950">
              {paragraphs.length ? paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>) : <p>{getSummary(newsItem)}</p>}
            </div>

            <div className="mt-10 flex flex-col gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <Link to="/news" className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-700 px-5 py-3 font-black text-white hover:bg-blue-800">
                <ArrowLeft className="h-4 w-4" /> Back to News
              </Link>
              <button
                type="button"
                onClick={() => navigator.share?.({ title: newsItem.title, url: window.location.href })}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 font-black text-slate-700 hover:bg-slate-100"
              >
                <Share2 className="h-4 w-4" /> Share Story
              </button>
            </div>
          </div>
        </article>

        <aside className="space-y-6">
          <div className="rounded-[2rem] bg-white p-6 shadow-lg shadow-slate-200/50 ring-1 ring-slate-200">
            <h2 className="text-lg font-black text-slate-950">Story Information</h2>
            <dl className="mt-5 space-y-4 text-sm">
              <div className="rounded-2xl bg-slate-50 p-4">
                <dt className="font-bold text-slate-500">Published</dt>
                <dd className="mt-1 font-black text-slate-950">{formatDate(newsDate(newsItem))}</dd>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <dt className="font-bold text-slate-500">Source</dt>
                <dd className="mt-1 font-black text-slate-950">{newsItem.author || "Federal Polytechnic Bida"}</dd>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <dt className="font-bold text-slate-500">Status</dt>
                <dd className="mt-1 font-black text-emerald-700">Published</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-[2rem] bg-slate-950 p-6 text-white shadow-lg shadow-slate-300/40">
            <h2 className="text-lg font-black">More Campus News</h2>
            <div className="mt-5 space-y-4">
              {related.length ? related.map((item) => (
                <Link key={item.id || item.slug} to={`/news/${item.slug || item.id}`} className="group block rounded-2xl border border-white/10 bg-white/5 p-4 hover:bg-white/10">
                  <p className="line-clamp-2 font-black text-white group-hover:text-blue-200">{item.title}</p>
                  <p className="mt-2 text-xs font-bold text-slate-400">{formatDate(newsDate(item))}</p>
                </Link>
              )) : (
                <p className="rounded-2xl bg-white/5 p-4 text-sm text-slate-300">No related news is available yet.</p>
              )}
            </div>
            <Link to="/news" className="mt-5 inline-flex items-center gap-2 text-sm font-black text-blue-200 hover:text-white">
              View all news <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </aside>
      </section>
    </main>
  );
}
